/**
 * atomberg-gtm-datalayer.js
 * ─────────────────────────
 * Pushes GTM dataLayer events matching the GTM-5V9BGM6 container's trigger
 * and variable definitions, ported from the Magento implementation.
 *
 * Events fired here:
 *   pageView · userLoaded · cartLoaded · UserData ·
 *   view_item · Category View · select_item ·
 *   Product_Color_Click · Product_Sweep_Size_Click · Product_Type_Click ·
 *   add_to_cart · remove_from_cart · view_cart · begin_checkout · purchase ·
 *   Rating_Click · Coupon_Strip_Click · Search_Btn_Click · Search_initiated ·
 *   scan_opened · scan_succeeded
 *
 * Warranty Registration tracking plan (atomberg-warranty-wizard.js /
 * atomberg-support-repeatable.js, via atomberg:warranty-event): clicked_
 * register_your_warranty · Warranty_mobile_number_entered · Clicked_Add_
 * Another_Product · entered_serial_number · entered_model_name ·
 * Clicked_Continue_Serial_Number · Uploaded_Invoice · Entered_Purchase_Date ·
 * Entered_Source_of_Purchase · Click_continue_Purchase_details ·
 * Entered_Name · Entered_Pincode · Clicked_Continue_Your_Details ·
 * View_Warranty_Review_Page
 *
 * Magic Checkout events bridged from atomberg-magic-checkout-analytics.js
 * (watchMagicEvents): otp_initiated · Coupon Applied · coupon code failed ·
 * payment failure · add_shipping_info · add_payment_info, built from
 * magic:<mx-analytics event name> CustomEvents. purchase is built from
 * magic:complete (window.onCompleteMagiCheckout).
 *
 * Field-name typos are intentional — GTM variables target these exact keys:
 *   ecommerce.quanitity  (view_cart, begin_checkout)
 *   ecommerce.coupen     (begin_checkout)
 *
 * Requires: atomberg-gtm.liquid loaded in <head>.
 * Data source: #atomberg-gtm-page-data JSON rendered by atomberg-gtm-page-data.liquid.
 */
(function () {
  'use strict';

  /** @type {any} */
  var w = window;
  if (w._atombergGTMInit) return;
  w._atombergGTMInit = true;

  w.dataLayer = w.dataLayer || [];

  // ── Page context ──────────────────────────────────────────────────────────
  /**
   * @type {{
   *   template: string,
   *   page: string,
   *   is_logged_in: boolean,
   *   customer: {id: number, email: string, phone: string, first_name: string, last_name: string} | null,
   *   product: {item_id: string, item_name: string, item_brand: string, item_category: string, item_variant: string, price: number, retail_price: number, discount: number, currency: string, quantity: number} | null
   * }}
   */
  var PAGE_DATA = (function () {
    var el = document.getElementById('atomberg-gtm-page-data');
    if (!el) return { template: '', page: '', is_logged_in: false, customer: null, product: null };
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (_) {
      return { template: '', page: '', is_logged_in: false, customer: null, product: null };
    }
  })();

  var isMobile = w.innerWidth < 750;

  /**
   * Run analytics work off the interaction's critical path — dataLayer.push
   * executes GTM tags synchronously, which must never sit between a tap and
   * its next paint (INP). Not used for events racing a navigation.
   * @param {() => void} fn
   */
  function deferPush(fn) {
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(fn, { timeout: 2000 });
    } else {
      setTimeout(fn, 0);
    }
  }

  // ── pageView ──────────────────────────────────────────────────────────────
  function firePageView() {
    w.dataLayer.push({
      event: 'pageView',
      page: PAGE_DATA.page || w.location.pathname,
      title: document.title,
      device: isMobile ? 'mobile' : 'desktop',
      is_logged_in: PAGE_DATA.is_logged_in,
    });
  }

  // ── userLoaded — user identity on every page load ─────────────────────────
  function fireUserLoaded() {
    var c = PAGE_DATA.customer;
    w.dataLayer.push({
      event: 'userLoaded',
      user: {
        loggedInStatus: PAGE_DATA.is_logged_in,
        email: c ? c.email || '' : '',
        firstname: c ? c.first_name || '' : '',
        lastname: c ? c.last_name || '' : '',
      },
    });
  }

  // ── UserData — fires enhanced conversion data when customer is identified ──
  function fireUserData() {
    var c = PAGE_DATA.customer;
    if (!c) return;
    w.dataLayer.push({
      event: 'UserData',
      user_status: 'logged_in',
      phone: c.phone || '',
      email: c.email || '',
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      city: '',
      state: '',
      device: isMobile ? 'mobile' : 'desktop',
      paymentMode: '',
      total_no_of_orders: 0,
      mode: '',
      date_of_birth: '',
      gender: '',
      address: '',
      last_order_date: '',
      loyalty_points: 0,
      total_order_value: 0,
      enhanced_conversion_data: {
        email: c.email || '',
        phone_number: c.phone || '',
        first_name: c.first_name || '',
        last_name: c.last_name || '',
      },
    });
  }

  // ── view_item ─────────────────────────────────────────────────────────────
  /**
   * @param {Record<string, any>} item
   */
  function fireViewItem(item) {
    var pdpItem = Object.assign(
      { item_list_id: 'PDP', item_list_name: 'Product Detail Page' },
      item
    );
    w.dataLayer.push({ ecommerce: null });
    w.dataLayer.push({
      event: 'view_item',
      pagePath: w.location.href,
      pageType: 'pdp',
      pageTitle: item.item_name || '',
      is_logged_in: PAGE_DATA.is_logged_in,
      device: isMobile ? 'mobile' : 'desktop',
      view_type: '',
      bought_in_variant: 'NOT_SHOWN',
      geolocation_AB: '',
      geolocation_pincode: '',
      emi_label_ab: '',
      emi_label_configured: false,
      ecommerce: {
        currency: 'INR',
        value: item.retail_price,
        items: [pdpItem],
        // Legacy UA for FB ViewContent tag
        detail: {
          products: [legacyProduct(item, 1)],
        },
      },
    });
  }

  // ── Category View ─────────────────────────────────────────────────────────
  function fireCategoryView() {
    /** @type {HTMLElement | null} */
    var heading = document.querySelector('.atomberg-ch__title');
    var categoryName = heading ? (heading.textContent || '').trim() : document.title;
    var productCards = document.querySelectorAll('[data-product-id]');
    w.dataLayer.push({
      event: 'Category View', // exact — uppercase + space, matches GTM trigger 222
      category: categoryName,
      page: PAGE_DATA.page,
      device: isMobile ? 'mobile' : 'desktop',
      is_logged_in: PAGE_DATA.is_logged_in,
      item_count: productCards.length,
    });
  }

  // ── Cart helpers ──────────────────────────────────────────────────────────
  // Last successful /cart.js response — Magic clears the cart right after placing
  // the order, so the purchase handler must read this snapshot, not a live fetch.
  /** @type {any} */
  var lastCart = null;
  // Last magic:shipping_selected detail — Razorpay's payment-success callback
  // carries no address, so the purchase handler reuses whatever address the
  // customer entered earlier in the same checkout session.
  /** @type {Record<string, any> | null} */
  var lastMagicShipping = null;
  function fetchCart() {
    var shared = w.AtombergCartState;
    var req = shared
      ? shared.getCart({ fresh: true })
      : fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
          return r.json();
        });
    return req.then(function (/** @type {any} */ cart) {
      lastCart = cart;
      return cart;
    });
  }

  /**
   * Build a GA4 item object from a /cart.js line item.
   * @param {any} item
   * @param {number} index
   * @param {number} qty
   * @returns {Record<string, any>}
   */
  function ga4Item(item, index, qty) {
    var variant = '';
    (item.options_with_values || []).forEach(function (/** @type {any} */ o) {
      variant = o.value;
    });
    var regPrice = (item.original_price || item.price || 0) / 100;
    var salePrice = (item.price || 0) / 100;
    return {
      id: item.sku || String(item.variant_id), // spec requires both id and item_id on every item
      item_id: item.sku || String(item.variant_id),
      item_name: item.product_title || item.title || '',
      item_brand: item.vendor || 'Atomberg',
      item_category: item.product_type || '',
      item_variant: variant,
      price: regPrice,
      retail_price: salePrice,
      discount: Math.max(0, parseFloat((regPrice - salePrice).toFixed(2))),
      currency: 'INR',
      quantity: qty,
      index: index,
    };
  }

  /**
   * Build the legacy UA product shape from a ga4Item result.
   * Required by FB pixel tags in GTM which read ecommerce.add/remove/detail/purchase.products.
   * @param {Record<string, any>} ga4
   * @param {number} qty
   * @returns {Record<string, any>}
   */
  function legacyProduct(ga4, qty) {
    return {
      name: ga4.item_name,
      id: ga4.item_id,
      sku: ga4.item_id,
      price: ga4.retail_price,
      variant: ga4.item_variant,
      quantity: qty,
      category: ga4.item_category,
    };
  }

  /**
   * @param {any} cart - /cart.js response
   * @returns {Record<string, {qty: number, item: any}>}
   */
  function cartSnapshot(cart) {
    /** @type {Record<string, {qty: number, item: any}>} */
    var map = {};
    (cart.items || []).forEach(function (/** @type {any} */ item) {
      map[String(item.variant_id)] = { qty: item.quantity || 0, item: item };
    });
    return map;
  }

  // ── cartLoaded — full cart snapshot on page load ───────────────────────────
  function fireCartLoaded() {
    fetchCart()
      .then(function (/** @type {any} */ cart) {
        var items = (cart.items || []).map(function (/** @type {any} */ item) {
          var variantLabel = (item.options_with_values || [])
            .map(function (/** @type {any} */ o) {
              return o.value;
            })
            .join(', ');
          return {
            name: item.product_title || item.title || '',
            price: (item.price || 0) / 100,
            sku: item.sku || String(item.variant_id),
            id: item.sku || String(item.variant_id),
            variant: variantLabel,
            quantity: item.quantity || 0,
            category: item.product_type || '',
          };
        });
        w.dataLayer.push({ event: 'cartLoaded', cart: { items: items, address: null } });
      })
      .catch(function () {});
  }

  // ── add_to_cart / remove_from_cart via cart diff ──────────────────────────
  /** @type {Record<string, {qty: number, item: any}> | null} */
  var baseline = null;
  var lock = fetchCart()
    .then(function (/** @type {any} */ cart) {
      baseline = cartSnapshot(cart);
    })
    .catch(function () {
      baseline = {};
    });

  document.addEventListener('cart:update', function () {
    lock = lock.then(function () {
      return fetchCart()
        .then(function (/** @type {any} */ cart) {
          var next = cartSnapshot(cart);
          if (baseline) diffAndFire(baseline, next);
          baseline = next;
        })
        .catch(function () {});
    });
  });

  /**
   * @param {Record<string, {qty: number, item: any}>} prev
   * @param {Record<string, {qty: number, item: any}>} next
   */
  function diffAndFire(prev, next) {
    Object.keys(next).forEach(function (vid) {
      var nextEntry = next[vid];
      if (!nextEntry) return;
      var prevEntry = prev[vid];
      var prevQty = prevEntry ? prevEntry.qty : 0;
      var delta = nextEntry.qty - prevQty;
      if (delta <= 0) return;
      var it = ga4Item(nextEntry.item, 0, delta);
      w.dataLayer.push({ ecommerce: null });
      w.dataLayer.push({
        event: 'add_to_cart',
        page: PAGE_DATA.page,
        is_logged_in: PAGE_DATA.is_logged_in,
        device: isMobile ? 'mobile' : 'desktop',
        bought_in_variant: 'NOT_SHOWN',
        emi_label_ab: '',
        emi_label_configured: false,
        ecommerce: {
          currency: 'INR',
          value: parseFloat((it.retail_price * delta).toFixed(2)),
          checkout_method: 'magic_checkout',
          items: [it],
          // Legacy UA for FB AddToCart tag
          add: { products: [legacyProduct(it, delta)] },
        },
      });
    });

    Object.keys(prev).forEach(function (vid) {
      var prevEntry = prev[vid];
      if (!prevEntry) return;
      var nextEntry = next[vid];
      var nextQty = nextEntry ? nextEntry.qty : 0;
      var delta = prevEntry.qty - nextQty;
      if (delta <= 0) return;
      var it = ga4Item(prevEntry.item, 0, delta);
      w.dataLayer.push({ ecommerce: null });
      w.dataLayer.push({
        event: 'remove_from_cart',
        is_logged_in: PAGE_DATA.is_logged_in,
        device: isMobile ? 'mobile' : 'desktop',
        bought_in_variant: 'NOT_SHOWN',
        emi_label_ab: '',
        emi_label_configured: false,
        ecommerce: {
          currency: 'INR',
          value: parseFloat((it.retail_price * delta).toFixed(2)),
          items: [it],
          // Legacy UA for FB RemoveFromCart tag
          remove: { products: [legacyProduct(it, delta)] },
        },
      });
    });
  }

  // ── view_cart ─────────────────────────────────────────────────────────────
  function fireViewCart() {
    fetchCart()
      .then(function (/** @type {any} */ cart) {
        if (!cart.items || !cart.items.length) return;
        var items = cart.items.map(function (/** @type {any} */ item, /** @type {number} */ i) {
          return ga4Item(item, i, item.quantity);
        });
        var totalQty = cart.items.reduce(function (
          /** @type {number} */ s,
          /** @type {any} */ item
        ) {
          return s + (item.quantity || 0);
        }, 0);
        w.dataLayer.push({ ecommerce: null });
        w.dataLayer.push({
          event: 'view_cart',
          is_logged_in: PAGE_DATA.is_logged_in,
          device: isMobile ? 'mobile' : 'desktop',
          ecommerce: {
            currency: 'INR',
            value: (cart.total_price || 0) / 100,
            quanitity: totalQty, // intentional typo — GTM variable dlv-ecommerce.quanitity reads this exact key
            items: items,
          },
        });
      })
      .catch(function () {});
  }

  // Cart drawer: fire view_cart each time the drawer dialog opens.
  function watchDrawer() {
    var el = document.querySelector('cart-drawer-component dialog');
    if (!el) return;
    var dialog = /** @type {HTMLDialogElement} */ (el);
    var obs = new MutationObserver(function () {
      if (dialog.open) fireViewCart();
    });
    obs.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  // ── begin_checkout ────────────────────────────────────────────────────────
  function fireBeginCheckout() {
    fetchCart()
      .then(function (/** @type {any} */ cart) {
        if (!cart.items || !cart.items.length) return;
        var items = cart.items.map(function (/** @type {any} */ item, /** @type {number} */ i) {
          return ga4Item(item, i, item.quantity);
        });
        var totalQty = cart.items.reduce(function (
          /** @type {number} */ s,
          /** @type {any} */ item
        ) {
          return s + (item.quantity || 0);
        }, 0);
        var totalDiscount =
          cart.items.reduce(function (/** @type {number} */ s, /** @type {any} */ item) {
            return s + ((item.original_price || item.price) - item.price) * item.quantity;
          }, 0) / 100;
        var discounts = cart.cart_level_discount_applications || [];
        var coupon = discounts.length ? discounts[0].title || '' : '';
        var savedPincode = (function () {
          try {
            return localStorage.getItem('atomberg_pincode') || '';
          } catch (_) {
            return '';
          }
        })();
        w.dataLayer.push({ ecommerce: null });
        w.dataLayer.push({
          event: 'begin_checkout',
          page: PAGE_DATA.page,
          paymentMode: 'magic_checkout',
          is_logged_in: PAGE_DATA.is_logged_in,
          device: isMobile ? 'mobile' : 'desktop',
          pincode: savedPincode,
          trigger_type: '',
          geolocation_AB: '',
          geolocation_pincode: '',
          popup_appeared: false,
          emi_label_ab: '',
          emi_label_configured: false,
          bought_in_variant: 'NOT_SHOWN',
          ecommerce: {
            value: (cart.total_price || 0) / 100,
            currency: 'INR',
            checkout_method: 'magic',
            discount: parseFloat(totalDiscount.toFixed(2)),
            quanitity: totalQty, // intentional typo — matches GTM variable
            coupen: coupon, // intentional typo — matches GTM variable
            items: items,
          },
        });
      })
      .catch(function () {});
  }

  // Capture phase — must run before the inline onclick navigates to checkout.
  document.addEventListener(
    'click',
    function (e) {
      var t = /** @type {HTMLElement} */ (e.target);
      var btn = t.closest(
        '#checkout, [onclick*="openRzpCheckout"], [onclick*="onBuyNowClickMagic"]'
      );
      if (btn) fireBeginCheckout();
    },
    true
  );

  /**
   * Map a line item from Razorpay's order object (order.shopify_cart /
   * order.customer_cart) to our GA4 item shape. Field names are inferred from
   * Razorpay's own internal cart-fallback formatter — needs live-payload
   * verification; falls back to /cart.js-style field names too since the
   * exact shape in the non-fallback path isn't confirmed.
   * @param {any} item
   * @param {number} index
   * @returns {Record<string, any>}
   */
  function orderItem(item, index) {
    var regPrice = parseFloat(item.original_price || item.price || 0) || 0;
    var salePrice = parseFloat(item.price || item.final_price || 0) || 0;
    return {
      id: item.sku || String(item.variant_id || item.product_id || ''),
      item_id: item.sku || String(item.variant_id || item.product_id || ''),
      item_name: item.title || item.name || item.product_title || '',
      item_brand: item.vendor || item.brand || 'Atomberg',
      item_category: item.product_type || item.type || '',
      item_variant: item.variant_id || '',
      price: regPrice,
      retail_price: salePrice,
      discount: Math.max(0, parseFloat((regPrice - salePrice).toFixed(2))),
      currency: 'INR',
      quantity: item.quantity || 1,
      index: index,
    };
  }

  // ── purchase — via Razorpay's onCompleteMagiCheckout callback ────────────
  // magic:complete is bridged from window.onCompleteMagiCheckout — a plain,
  // optional global Razorpay calls directly on order completion, requiring no
  // wrapping or interception (see atomberg-magic-checkout-analytics.js). It
  // carries the full order object (customer_details, shipping_fee, total_tax,
  // promotions, cart line items) — far richer than checkoutOptions.handler's
  // sparse {razorpay_payment_id, razorpay_order_id, razorpay_signature}, and
  // doesn't depend on caching state from earlier funnel events. lastCart /
  // lastMagicShipping are kept as fallbacks only, in case any field is absent
  // from the order object in practice.
  //
  // mx-analytics does not emit a purchase event for this integration, and the
  // previous magic.checkout_data/payment_successful window-event hooks never
  // fired at all (confirmed via a live capture during a full checkout).
  //
  // KNOWN GAP: orderId below is Razorpay's own order id (order_XXXXXXXXX), not
  // Shopify's order name (#1234) — Shopify's order doesn't exist yet at the
  // moment this callback fires. If GTM/Meta need the Shopify order name
  // specifically, this needs a different signal.
  w.addEventListener('magic:complete', function (/** @type {any} */ e) {
    var d = e.detail || {};
    var order = d.order || {};
    var cust = order.customer_details || /** @type {any} */ ({});
    var ship = lastMagicShipping || /** @type {any} */ ({});
    var cartSource = order.shopify_cart || order.customer_cart || {};
    var rawItems = cartSource.line_items || cartSource.items || [];
    var cartItems = rawItems.length ? rawItems : lastCart && lastCart.items ? lastCart.items : [];
    var items = rawItems.length
      ? rawItems.map(orderItem)
      : cartItems.map(function (/** @type {any} */ item, /** @type {number} */ i) {
          return ga4Item(item, i, item.quantity);
        });
    var revenue = d.totalAmount
      ? d.totalAmount / 100
      : lastCart
        ? (lastCart.total_price || 0) / 100
        : 0;
    var orderId = String(d.orderId || order.order_id || '');
    var email = cust.email || ship.email || '';
    var phone = cust.contact || cust.phone || ship.phone || ship.phone_number || '';
    var firstName = cust.first_name || cust.firstName || ship.first_name || ship.firstName || '';
    var lastName = cust.last_name || cust.lastName || ship.last_name || ship.lastName || '';
    var address =
      cust.address ||
      cust.line1 ||
      cust.address1 ||
      ship.line1 ||
      ship.address1 ||
      ship.address ||
      '';
    var zip =
      cust.zipcode ||
      cust.pincode ||
      cust.zip ||
      ship.zipcode ||
      ship.pincode ||
      ship.zip ||
      (function () {
        try {
          return localStorage.getItem('atomberg_pincode') || '';
        } catch (_) {
          return '';
        }
      })();
    var city = cust.city || ship.city || '';
    var region = cust.state || cust.province || ship.state || ship.province || '';
    // shipping_fee/total_tax confirmed on the order object passed to
    // onCompleteMagiCheckout; shippingAmount/ship.shipping_fee kept as
    // fallbacks from the cached shipping_selected payload.
    var shippingAmt = (order.shipping_fee || ship.shipping_fee || ship.shippingAmount || 0) / 100;
    var tax = (order.total_tax || 0) / 100 || (lastCart ? (lastCart.total_tax || 0) / 100 : 0);
    var coupon =
      (order.promotions || [])
        .map(function (/** @type {any} */ p) {
          return p.code || '';
        })
        .filter(Boolean)
        .join('|') ||
      (lastCart && (lastCart.cart_level_discount_applications || []).length
        ? lastCart.cart_level_discount_applications[0].title || ''
        : '');
    var totalQty = items.reduce(function (/** @type {number} */ s, /** @type {any} */ it) {
      return s + (it.quantity || 0);
    }, 0);
    var totalDiscount =
      cartItems.reduce(function (/** @type {number} */ s, /** @type {any} */ item) {
        return s + ((item.original_price || item.price) - item.price) * (item.quantity || 1);
      }, 0) / 100;

    // SHA-256 hash of '91' + 10-digit phone for userId (Google Ads enhanced conversions)
    var rawPhone = phone
      .replace(/^\+?91/, '')
      .replace(/\D/g, '')
      .slice(-10);
    var userIdPromise =
      rawPhone && w.crypto && w.crypto.subtle
        ? w.crypto.subtle
            .digest('SHA-256', new TextEncoder().encode('91' + rawPhone))
            .then(function (/** @type {ArrayBuffer} */ buf) {
              return Array.from(new Uint8Array(buf))
                .map(function (b) {
                  return b.toString(16).padStart(2, '0');
                })
                .join('');
            })
            .catch(function () {
              return '';
            })
        : Promise.resolve('');

    userIdPromise.then(function (/** @type {string} */ userId) {
      // Mark this order as tracked so the thank-you page snippet doesn't double-fire.
      try {
        sessionStorage.setItem('atomberg_gtm_purchase_' + orderId, '1');
      } catch (_) {}
      w.dataLayer.push({ ecommerce: null });
      w.dataLayer.push({
        event: 'purchase',
        userId: userId,
        transaction_id: orderId,
        order_value: revenue,
        order_id: orderId,
        checkout_method: 'magic',
        is_logged_in: PAGE_DATA.is_logged_in,
        bought_in_variant: 'NOT_SHOWN',
        device: isMobile ? 'mobile' : 'desktop',
        emi_label_ab: '',
        emi_label_configured: false,
        enhanced_conversion_data: { email: email },
        phone_number: phone,
        first_name: firstName,
        last_name: lastName,
        address: address,
        zip_code: zip,
        city: city,
        ecommerce: {
          value: revenue,
          currency: 'INR',
          checkout_method: 'magic',
          discount: parseFloat(totalDiscount.toFixed(2)),
          quanitity: totalQty, // intentional typo — matches GTM variable
          email_id: email,
          first_name: firstName,
          last_name: lastName,
          phone_number: phone,
          address: address,
          zip_code: zip,
          city: city,
          region: region,
          transaction_id: orderId,
          coupon: coupon,
          items: items,
          // Legacy UA for FB Purchase tag
          purchase: {
            actionField: {
              userId: userId,
              id: orderId,
              affiliation: 'Online Store',
              revenue: revenue,
              tax: tax,
              shipping: shippingAmt,
              coupon: coupon,
            },
            products: items.map(function (/** @type {any} */ it) {
              return legacyProduct(it, it.quantity);
            }),
          },
        },
      });
    });
  });

  // ── Product option click tracking (PDP) ───────────────────────────────────
  // Normalized option name for each option index (populated during init).
  /** @type {Array<string>} */
  var optionNames = [];
  // Previous variant's option values — used to detect which option changed.
  /** @type {Array<string>} */
  var prevOpts = ['', '', ''];

  /**
   * Build optionNames[] from [data-option-index] groups and seed prevOpts[].
   * Must run after DOM is ready.
   */
  function initOptionTracking() {
    if (PAGE_DATA.template !== 'product') return;
    var picker = document.querySelector('[data-atomberg-pdp-variant-picker]');
    if (!picker) return;
    picker.querySelectorAll('[data-option-index]').forEach(function (/** @type {Element} */ group) {
      var idx = parseInt(group.getAttribute('data-option-index') || '0', 10);
      var labelEl = group.querySelector('.atomberg-product-variant-picker__option-label-key');
      optionNames[idx] = labelEl
        ? (labelEl.textContent || '').replace(':', '').trim().toLowerCase()
        : '';
    });
    // Seed prevOpts from the URL ?variant= param or the first variant.
    var variants = [];
    try {
      variants = JSON.parse(picker.getAttribute('data-variants') || '[]');
    } catch (_) {}
    var urlVid = new URLSearchParams(w.location.search).get('variant');
    var initV =
      (urlVid &&
        variants.find(function (/** @type {any} */ v) {
          return String(v.id) === urlVid;
        })) ||
      variants[0];
    if (initV) prevOpts = [initV.option1 || '', initV.option2 || '', initV.option3 || ''];
  }

  /**
   * Return the current value for the option whose normalized name matches pattern.
   * @param {RegExp} pattern
   * @param {Array<string>} opts
   * @returns {string}
   */
  function optValueByName(pattern, opts) {
    for (var i = 0; i < optionNames.length; i++) {
      if (pattern.test(optionNames[i] || '')) return opts[i] || '';
    }
    return '';
  }

  // atomberg-product-variant-picker.js dispatches 'atomberg:pdp-variant-change' (bubbles:true).
  // Handle both view_item (on every variant switch) and Product_*_Click (detects which option changed).
  document.addEventListener('atomberg:pdp-variant-change', function (/** @type {any} */ e) {
    if (PAGE_DATA.template !== 'product') return;
    var variant = (e.detail || {}).variant;
    if (!variant) return;
    // Deferred callbacks run FIFO, so prevOpts still updates in click order.
    deferPush(function () {
      // ── view_item ────────────────────────────────────────────────────────────
      var regPrice = (variant.compare_at_price || variant.price || 0) / 100;
      var salePrice = (variant.price || 0) / 100;
      /** @type {Record<string, any>} */
      var it = {
        item_id: variant.sku || String(variant.id),
        item_name: PAGE_DATA.product ? PAGE_DATA.product.item_name : '',
        item_brand: PAGE_DATA.product ? PAGE_DATA.product.item_brand : 'Atomberg',
        item_category: PAGE_DATA.product ? PAGE_DATA.product.item_category : '',
        item_variant: variant.title || '',
        price: regPrice,
        retail_price: salePrice,
        discount: Math.max(0, parseFloat((regPrice - salePrice).toFixed(2))),
        currency: 'INR',
        quantity: 1,
      };
      fireViewItem(it);

      // ── Product_*_Click ───────────────────────────────────────────────────────
      var newOpts = [variant.option1 || '', variant.option2 || '', variant.option3 || ''];
      var changedIdx = -1;
      newOpts.forEach(function (val, i) {
        if (val !== prevOpts[i]) changedIdx = i;
      });

      if (changedIdx >= 0 && optionNames.length) {
        var optName = optionNames[changedIdx] || '';
        var evtName = '';
        if (/colou?r/.test(optName)) evtName = 'Product_Color_Click';
        else if (/sweep|size/.test(optName)) evtName = 'Product_Sweep_Size_Click';
        else if (/type/.test(optName)) evtName = 'Product_Type_Click';

        if (evtName) {
          w.dataLayer.push({
            event: evtName,
            page: w.location.href,
            category: PAGE_DATA.product ? PAGE_DATA.product.item_category : '',
            name: PAGE_DATA.product ? PAGE_DATA.product.item_name : '',
            fan_type: optValueByName(/type/, newOpts),
            sweep_size: optValueByName(/sweep|size/, newOpts),
            colour: optValueByName(/colou?r/, newOpts),
            id: variant.sku || String(variant.id),
          });
        }
      }
      prevOpts = newOpts;
    });
  });

  // ── select_item — product card click on collection / search pages ─────────
  // data-product-type is rendered on the card root by atomberg-product-card.liquid.
  document.addEventListener('click', function (/** @type {MouseEvent} */ e) {
    var t = /** @type {HTMLElement} */ (e.target);
    // Exclude ATC and delivery-check actions.
    if (t.closest('[data-card-atc], [data-adc-action], button')) return;
    var link = /** @type {HTMLElement|null} */ (t.closest('a[href]'));
    if (!link) return;
    var cardEl = /** @type {HTMLElement|null} */ (link.closest('[data-product-id]'));
    if (!cardEl) return;
    var card = cardEl;

    deferPush(function () {
      try {
        var variants = JSON.parse(card.getAttribute('data-variants') || '[]');
        var variantIdEl = card.querySelector('[data-card-variant-id]');
        var variantId = variantIdEl ? variantIdEl.getAttribute('data-card-variant-id') : '';
        var v =
          variants.find(function (/** @type {any} */ x) {
            return String(x.id) === String(variantId);
          }) || variants[0];
        if (!v) return;

        var titleEl = card.querySelector('[data-card-title-link]');
        var itemName = titleEl ? (titleEl.textContent || '').trim() : '';
        var regP = (v.compare_at_price || v.price || 0) / 100;
        var saleP = (v.price || 0) / 100;

        w.dataLayer.push({ ecommerce: null });
        w.dataLayer.push({
          event: 'select_item',
          page: w.location.pathname,
          pageType: 'plp',
          pageTitle: itemName,
          is_logged_in: PAGE_DATA.is_logged_in,
          device: isMobile ? 'mobile' : 'desktop',
          view_type: '',
          ecommerce: {
            currency: 'INR',
            items: [
              {
                item_id: v.sku || String(v.id),
                item_name: itemName,
                item_brand: 'Atomberg',
                item_category: card.getAttribute('data-product-type') || '',
                item_variant: v.title || '',
                price: regP,
                retail_price: saleP,
                discount: Math.max(0, parseFloat((regP - saleP).toFixed(2))),
                currency: 'INR',
                quantity: 1,
              },
            ],
          },
        });
      } catch (_) {}
    });
  });

  // ── view_item_list — all visible product cards on collection / search page ──
  function fireViewItemList() {
    var isSearchPage = /\/search/.test(w.location.pathname);
    if (PAGE_DATA.template !== 'collection' && !isSearchPage) return;
    var cards = document.querySelectorAll('[data-product-id]');
    if (!cards.length) return;
    /** @type {Array<Record<string, any>>} */
    var items = [];
    /** @type {Array<Record<string, any>>} */
    var impressions = [];
    cards.forEach(function (/** @type {Element} */ card, /** @type {number} */ i) {
      try {
        var variants = JSON.parse(card.getAttribute('data-variants') || '[]');
        var v = variants[0];
        if (!v) return;
        var titleEl = card.querySelector('[data-card-title-link]');
        var name = titleEl ? (titleEl.textContent || '').trim() : '';
        var sku = v.sku || String(v.id);
        var category = card.getAttribute('data-product-type') || '';
        var regP = (v.compare_at_price || v.price || 0) / 100;
        var saleP = (v.price || 0) / 100;
        items.push({
          item_id: sku,
          item_name: name,
          item_brand: 'Atomberg',
          item_category: category,
          price: regP,
          retail_price: saleP,
          discount: Math.max(0, parseFloat((regP - saleP).toFixed(2))),
          currency: 'INR',
          quantity: 1,
        });
        // Legacy UA impressions required by FB / UA tags
        impressions.push({
          name: name,
          id: sku,
          sku: sku,
          price: saleP,
          category: category,
          position: i + 1,
        });
      } catch (_) {}
    });
    if (!items.length) return;
    w.dataLayer.push({ ecommerce: null });
    w.dataLayer.push({
      event: 'view_item_list',
      is_logged_in: PAGE_DATA.is_logged_in,
      device: isMobile ? 'mobile' : 'desktop',
      view_type: '',
      emi_label_ab: '',
      emi_label_configured: false,
      ecommerce: {
        currency: 'INR',
        impressions: impressions,
        items: items,
      },
    });
  }

  // ── Search_Btn_Click + search_popup_visible ───────────────────────────────
  // dialog:open is dispatched on dialog-component itself (bubbles: false),
  // so document.addEventListener never fires. Must attach directly to the element.
  function watchSearchDialog() {
    var dialogs = document.querySelectorAll('dialog-component');
    dialogs.forEach(function (/** @type {Element} */ d) {
      if (!d.querySelector('predictive-search-component')) return;
      d.addEventListener('dialog:open', function () {
        w.dataLayer.push({
          event: 'Search_Btn_Click',
          page: w.location.pathname,
          name: PAGE_DATA.product ? PAGE_DATA.product.item_name : '',
          category: PAGE_DATA.product ? PAGE_DATA.product.item_category : '',
          device: isMobile ? 'mobile' : 'desktop',
        });
        w.dataLayer.push({
          event: 'search_popup_visible',
          page: w.location.pathname,
          device: isMobile ? 'mobile' : 'desktop',
        });
      });
    });
  }

  // ── Search_popup_click — user clicks a result inside the search overlay ────
  document.addEventListener('click', function (/** @type {MouseEvent} */ e) {
    var t = /** @type {HTMLElement} */ (e.target);
    var resultEl = /** @type {HTMLElement|null} */ (
      t.closest('predictive-search-component a[href], predictive-search-component button')
    );
    if (!resultEl) return;
    var result = resultEl;
    deferPush(function () {
      var nameEl =
        result.querySelector(
          '.predictive-search-results__card-title, .predictive-search-results__pill, h3, h4'
        ) || result;
      var name = (nameEl.textContent || '').trim();
      var sectionEl = /** @type {HTMLElement|null} */ (
        t.closest('[class*="predictive-search-results__wrapper"]')
      );
      var section = '';
      if (sectionEl) {
        var cls = sectionEl.className || '';
        if (/product/.test(cls)) section = 'products';
        else if (/quer/.test(cls)) section = 'suggestions';
        else section = 'results';
      }
      w.dataLayer.push({
        event: 'search_popup_click',
        page: w.location.pathname,
        name: name,
        category: PAGE_DATA.product ? PAGE_DATA.product.item_category : '',
        search_section: section,
        device: isMobile ? 'mobile' : 'desktop',
      });
    });
  });

  // ── Search_initiated — search form submitted ───────────────────────────────
  document.addEventListener('submit', function (/** @type {Event} */ e) {
    var form = /** @type {HTMLFormElement} */ (e.target);
    if (!form || form.getAttribute('action') !== '/search') return;
    w.dataLayer.push({
      event: 'Search_initiated',
      page: w.location.pathname,
      device: isMobile ? 'mobile' : 'desktop',
    });
  });

  // ── Join_Live_Demo_Btn_Click — live demo CTA (PDP + landing pages) ─────────
  // Buttons must carry data-join-live-demo="<product-type>" e.g. "MG", "WP".
  // Generic + product-specific events both fire so GTM's sub-triggers work.
  document.addEventListener('click', function (/** @type {MouseEvent} */ e) {
    var t = /** @type {HTMLElement} */ (e.target);
    var demoBtn = /** @type {HTMLElement|null} */ (t.closest('[data-join-live-demo]'));
    if (!demoBtn) return;
    var btn = demoBtn;
    deferPush(function () {
      var productType = btn.getAttribute('data-join-live-demo') || '';
      // On collection pages PAGE_DATA.product is null — fall back to data attributes on the element.
      var sku = PAGE_DATA.product
        ? PAGE_DATA.product.item_id
        : btn.getAttribute('data-product-sku') || '';
      var name = PAGE_DATA.product
        ? PAGE_DATA.product.item_name
        : btn.getAttribute('data-product-name') || '';
      var payload = {
        id: sku,
        page_section: btn.getAttribute('data-page-section') || '',
        name: name,
        phone_number: PAGE_DATA.customer ? PAGE_DATA.customer.phone || '' : '',
        page: w.location.href,
        utm_source: (function () {
          try {
            return localStorage.getItem('lt_utm_source') || '';
          } catch (_) {
            return '';
          }
        })(),
        utm_medium: (function () {
          try {
            return localStorage.getItem('lt_utm_medium') || '';
          } catch (_) {
            return '';
          }
        })(),
        utm_campaign: (function () {
          try {
            return localStorage.getItem('lt_utm_campaign') || '';
          } catch (_) {
            return '';
          }
        })(),
      };
      w.dataLayer.push(Object.assign({ event: 'Join_Live_Demo_Btn_Click' }, payload));
      // Product-specific variant (MG / WP / SL / CPJ) for GTM's sub-triggers
      var specificEvent = productType ? 'Join_Live_Demo_' + productType : '';
      if (specificEvent) w.dataLayer.push(Object.assign({ event: specificEvent }, payload));
    });
  });

  // ── Atomberg_Benefits_Click — benefit tile / icon on PDP ─────────────────
  // Elements must carry data-atomberg-benefit="<benefit label>".
  document.addEventListener('click', function (/** @type {MouseEvent} */ e) {
    var t = /** @type {HTMLElement} */ (e.target);
    var benefitEl = /** @type {HTMLElement|null} */ (t.closest('[data-atomberg-benefit]'));
    if (!benefitEl) return;
    var el = benefitEl;
    deferPush(function () {
      w.dataLayer.push({
        event: 'Atomberg_Benefits_Click',
        page: w.location.pathname,
        benefit: el.getAttribute('data-atomberg-benefit') || (el.textContent || '').trim(),
        category: PAGE_DATA.product ? PAGE_DATA.product.item_category : '',
        device: isMobile ? 'mobile' : 'desktop',
      });
    });
  });

  // ── Rating_Click ──────────────────────────────────────────────────────────
  document.addEventListener('click', function (/** @type {MouseEvent} */ e) {
    var t = /** @type {HTMLElement} */ (e.target);
    if (!t.closest('[data-atomberg-rating], .atomberg-rating, [data-rating]')) return;
    deferPush(function () {
      w.dataLayer.push({
        event: 'Rating_Click',
        page: w.location.pathname,
        name: PAGE_DATA.product ? PAGE_DATA.product.item_name : '',
        category: PAGE_DATA.product ? PAGE_DATA.product.item_category : '',
        device: isMobile ? 'mobile' : 'desktop',
      });
    });
  });

  // ── Coupon_Strip_Click ────────────────────────────────────────────────────
  document.addEventListener('click', function (/** @type {MouseEvent} */ e) {
    var t = /** @type {HTMLElement} */ (e.target);
    var stripEl = /** @type {HTMLElement|null} */ (
      t.closest('[data-coupon-strip], .atomberg-coupon, .atomberg-offers')
    );
    if (!stripEl) return;
    var strip = stripEl;
    deferPush(function () {
      w.dataLayer.push({
        event: 'Coupon_Strip_Click',
        page: w.location.pathname,
        name: PAGE_DATA.product ? PAGE_DATA.product.item_name : '',
        category: PAGE_DATA.product ? PAGE_DATA.product.item_category : '',
        coupon_type: strip.getAttribute('data-coupon-type') || '',
        device: isMobile ? 'mobile' : 'desktop',
      });
    });
  });

  // ── scan_opened / scan_succeeded ────────────────────────────────────────────
  // atomberg-warranty-scan.js dispatches these for Register Warranty's
  // barcode/QR camera scanner. device_type comes from the event detail (set
  // there via userAgent) rather than the isMobile/innerWidth check used
  // elsewhere in this file, so GTM and MoEngage report the identical value
  // for the same real scan instead of two independently-computed guesses.
  document.addEventListener('atomberg:warranty-scan-opened', function (/** @type {any} */ e) {
    var d = e.detail || {};
    w.dataLayer.push({
      event: 'scan_opened',
      page: w.location.pathname,
      detection_method: d.detection_method || '',
      device_type: d.device_type || '',
      product_position: d.product_position || 1,
      total_products_in_form: d.total_products_in_form || 1,
      event_time: new Date().toISOString(),
    });
  });

  document.addEventListener('atomberg:warranty-scan-succeeded', function (/** @type {any} */ e) {
    var d = e.detail || {};
    w.dataLayer.push({
      event: 'scan_succeeded',
      page: w.location.pathname,
      detection_method: d.detection_method || '',
      device_type: d.device_type || '',
      barcode_format: d.barcode_format || '',
      duration_ms: d.duration_ms || 0,
      serial_number: d.serial_number || '',
      product_position: d.product_position || 1,
      total_products_in_form: d.total_products_in_form || 1,
      event_time: new Date().toISOString(),
    });
  });

  // ── atomberg:warranty-event → Warranty Registration tracking plan ───────
  // Generic pipe for the 13 non-scan events atomberg-warranty-wizard.js and
  // atomberg-support-repeatable.js fire (fireWarrantyEvent/fireWarranty) —
  // one listener instead of 13 named ones. event_date_time/page_url are
  // attached here rather than at each dispatch site.
  document.addEventListener('atomberg:warranty-event', function (/** @type {any} */ e) {
    var d = e.detail || {};
    if (!d.name) return;
    w.dataLayer.push(
      Object.assign(
        {
          event: d.name,
          event_date_time: new Date().toISOString(),
          page_url: w.location.href,
        },
        d.payload || {}
      )
    );
  });

  // ── Magic Checkout event bridge ───────────────────────────────────────────
  // Magic Checkout does NOT dispatch window CustomEvents for its funnel steps
  // (confirmed by Razorpay POC + a live capture that showed nothing but
  // magic.checkout_data firing). Its real transport is 'mx-analytics' on the
  // Razorpay SDK instance it creates internally, which we can't attach to
  // directly — atomberg-magic-checkout-analytics.js intercepts window.Razorpay
  // to get that instance and re-dispatches each event onto window as
  // magic:<event-name> (Razorpay's own names, unmodified). We listen for those
  // here and build the exact GTM event names/payload keys already trained on
  // (from the Magento migration) — no renaming.
  function watchMagicEvents() {
    /**
     * @param {any} e
     * @returns {Record<string, any>}
     */
    function detail(e) {
      return (e && e.detail) || {};
    }

    /**
     * Map a raw mx-analytics lineItems entry to our GA4 item shape.
     * Field names inferred from Razorpay's own internal formatter (needs
     * live-payload verification) — falls back gracefully if names differ.
     * @param {any} item
     * @param {number} index
     * @returns {Record<string, any>}
     */
    function magicItem(item, index) {
      var regPrice = parseFloat(item.original_price || item.mrp || item.price || 0) || 0;
      var salePrice = parseFloat(item.price || item.final_price || 0) || 0;
      return {
        id: item.sku || item.item_id || String(item.product_id || item.variant_id || ''),
        item_id: item.sku || item.item_id || String(item.product_id || item.variant_id || ''),
        item_name: item.title || item.item_name || item.name || '',
        item_brand: item.brand || item.vendor || item.item_brand || 'Atomberg',
        item_category: item.type || item.product_type || item.item_category || '',
        item_variant: item.variant_id || item.item_variant || '',
        price: regPrice,
        retail_price: salePrice,
        discount: Math.max(0, parseFloat((regPrice - salePrice).toFixed(2))),
        currency: 'INR',
        quantity: item.quantity || 1,
        index: index,
      };
    }

    /**
     * Shared ecommerce payload for the mx-analytics funnel events — amounts
     * arrive in paise; lineItems come straight from Magic, no cart fetch needed.
     * @param {any} d
     * @returns {Record<string, any>}
     */
    function magicEcommerce(d) {
      var items = (d.lineItems || []).map(magicItem);
      var totalQty = items.reduce(function (/** @type {number} */ s, /** @type {any} */ it) {
        return s + (it.quantity || 0);
      }, 0);
      return {
        currency: 'INR',
        value: (d.totalAmount || 0) / 100,
        discount: (d.couponDiscountValue || 0) / 100,
        quanitity: totalQty, // intentional typo — matches GTM variable
        coupen: d.appliedCouponCode || d.couponCode || '', // intentional typo — matches GTM variable
        items: items,
      };
    }

    // otp_initiated — mx-analytics fires 'otp_submitted' when the OTP flow starts
    w.addEventListener('magic:otp_submitted', function (/** @type {any} */ e) {
      var d = detail(e);
      w.dataLayer.push({
        event: 'otp_initiated',
        checkout_method: 'magic_checkout',
        device: isMobile ? 'mobile' : 'desktop',
        phone: d.phone || d.mobile || '',
        email: d.email || '',
        first_name: d.first_name || d.firstName || '',
        last_name: d.last_name || d.lastName || '',
        state: d.state || '',
        city: d.city || '',
        totalAmount: (d.totalAmount || 0) / 100,
        latestTotal: (d.latestTotal || 0) / 100,
        couponDiscountValue: (d.couponDiscountValue || 0) / 100,
        currency: 'INR',
        lineItems: d.lineItems || [],
        otp_verified: false,
        isScriptCouponApplied: false,
      });
    });

    // Coupon Applied
    w.addEventListener('magic:coupon_applied', function (/** @type {any} */ e) {
      var d = detail(e);
      var ecommerce = magicEcommerce(d);
      w.dataLayer.push({
        event: 'Coupon Applied', // exact name — GTM trigger matches this
        checkout_method: 'magic_checkout',
        paymentMode: 'magic_checkout',
        device: isMobile ? 'mobile' : 'desktop',
        coupon_code: d.appliedCouponCode || d.couponCode || '',
        cart_value_before_discount: ecommerce.value + ecommerce.discount,
        cart_value_after_discount: ecommerce.value,
        discount_amount: ecommerce.discount,
      });
    });

    // coupon code failed
    w.addEventListener('magic:coupon_failed', function (/** @type {any} */ e) {
      var d = detail(e);
      w.dataLayer.push({
        event: 'coupon code failed', // exact name — GTM trigger matches this
        checkout_method: 'magic_checkout',
        paymentMode: 'magic_checkout',
        device: isMobile ? 'mobile' : 'desktop',
        coupon_code: d.couponCode || d.appliedCouponCode || '',
        message: d.errorMsg || d.message || '',
        page_url: w.location.href,
      });
    });

    // payment failure
    w.addEventListener('magic:payment_failed', function (/** @type {any} */ e) {
      var d = detail(e);
      w.dataLayer.push({
        event: 'payment failure', // exact name — GTM trigger matches this
        checkout_method: 'magic_checkout',
        paymentMode: 'magic_checkout',
        device: isMobile ? 'mobile' : 'desktop',
        reason: d.reason || d.description || d.errorMsg || '',
        payment_mode: d.paymentMode || d.payment_mode || d.method || '',
        total_amount: (d.totalAmount || 0) / 100,
        checkout_user_info_submitted: d.checkout_user_info_submitted || false,
        phone_number: d.phone_number || d.phone || '',
        'Product URL': w.location.href,
      });
    });

    // add_shipping_info — mx-analytics fires 'shipping_selected'
    w.addEventListener('magic:shipping_selected', function (/** @type {any} */ e) {
      var d = detail(e);
      lastMagicShipping = d;
      var ecommerce = Object.assign(
        {
          email_id: d.email || '',
          first_name: d.first_name || d.firstName || '',
          last_name: d.last_name || d.lastName || '',
          phone_number: d.phone || d.phone_number || '',
          address: d.line1 || d.address1 || d.address || '',
          zip_code: d.zipcode || d.pincode || d.zip || '',
          city: d.city || '',
          region: d.state || d.province || '',
        },
        magicEcommerce(d)
      );
      w.dataLayer.push({ ecommerce: null });
      w.dataLayer.push({
        event: 'add_shipping_info',
        checkout_method: 'magic_checkout',
        paymentMode: 'magic_checkout',
        trigger_type: '',
        popup_appeared: false,
        is_logged_in: PAGE_DATA.is_logged_in,
        bought_in_variant: 'NOT_SHOWN',
        device: isMobile ? 'mobile' : 'desktop',
        ecommerce: ecommerce,
      });
      // checkout_user_info_submitted fires on the same address event
      w.dataLayer.push({
        event: 'checkout_user_info_submitted',
        checkout_method: 'magic_checkout',
        paymentMode: 'magic_checkout',
        device: isMobile ? 'mobile' : 'desktop',
        is_logged_in: PAGE_DATA.is_logged_in,
        name: (
          (d.first_name || d.firstName || '') +
          ' ' +
          (d.last_name || d.lastName || '')
        ).trim(),
        email: d.email || '',
        phone: d.phone || d.phone_number || '',
        address: d.line1 || d.address1 || d.address || '',
        city: d.city || '',
        state: d.state || d.province || '',
        pincode: d.zipcode || d.pincode || d.zip || '',
        order_amount: ecommerce.value,
        ecommerce: ecommerce,
      });
    });

    // add_payment_info — mx-analytics fires 'payment_initiated'
    w.addEventListener('magic:payment_initiated', function (/** @type {any} */ e) {
      var d = detail(e);
      var ecommerce = Object.assign(
        { payment_type: d.paymentMode || d.payment_method || d.method || '' },
        magicEcommerce(d)
      );
      w.dataLayer.push({ ecommerce: null });
      w.dataLayer.push({
        event: 'add_payment_info',
        checkout_method: 'magic_checkout',
        paymentMode: 'magic_checkout',
        popup_appeared: false,
        is_logged_in: PAGE_DATA.is_logged_in,
        bought_in_variant: 'NOT_SHOWN',
        device: isMobile ? 'mobile' : 'desktop',
        pincode: d.zipcode || d.pincode || d.zip || '',
        geolocation_AB: '',
        ecommerce: ecommerce,
      });
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  function start() {
    firePageView();
    fireUserLoaded();
    fireCartLoaded();
    if (PAGE_DATA.customer) fireUserData();
    if (PAGE_DATA.template === 'product' && PAGE_DATA.product) {
      fireViewItem(PAGE_DATA.product);
      initOptionTracking();
    }
    if (PAGE_DATA.template === 'collection') fireCategoryView();
    fireViewItemList();
    if (/\/cart(\/|$|\?)/.test(w.location.pathname)) fireViewCart();
    watchDrawer();
    watchSearchDialog();
    watchMagicEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', initOptionTracking);
  document.addEventListener('shopify:block:load', initOptionTracking);
})();
